// All imports at the top - this fixes the ESLint error
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Box, Container, Alert, CircularProgress, Typography, Grid } from '@mui/material';
import moment from 'moment';
import 'moment/locale/th';

// Import existing components
import EventDetailHeader from './EventDetailHeader.jsx';
import EventActionButtons from './EventActionButtons.js';
import EventImageCarousel from './EventImageCarousel.jsx';
import DualGroupChat from '../common/DualGroupChat.js';
import EventParticipants from './EventParticipants.jsx';

// Import styled components
import { StyledContainer, ContentWrapper, StyledPaper, EventTitle, EventSummary } from './styles.js';

// Import icons for inline EventContent (since we're moving chat before EventContent)
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import MapIcon from '@mui/icons-material/Map';
import DescriptionIcon from '@mui/icons-material/Description';

// Import additional styled components for inline content
import {
  EventImage,
  InfoBox,
  InfoItem,
  SectionTitle,
  SectionBody,
  StyledLink,
  MapContainer,
  MapHeaderContainer,
  MapFrame,
  DetailsContainer
} from './styles';

// Auth context with fallback (moved after imports)
let useAuth;
try {
  const authModule = require('../Login/AuthContext');
  useAuth = authModule.useAuth;
} catch (error) {
  console.warn('AuthContext not found, using fallback auth');
  useAuth = () => ({
    isAuthenticated: false,
    currentUser: null
  });
}

// API URL configuration (moved after imports)
const API_URL = process.env.REACT_APP_API_URL ;

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, currentUser } = useAuth();

  // State for the currently displayed event's data
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for user participation status
  const [participationStatus, setParticipationStatus] = useState(null);
  const [userEventGroups, setUserEventGroups] = useState(null);
  const [participantsRefreshTrigger, setParticipantsRefreshTrigger] = useState(0);

  // State for the filters in the EventDetailHeader
  const [headerType, setHeaderType] = useState('Category');
  const [headerStartDate, setHeaderStartDate] = useState(() => moment().format('YYYY-MM-DD'));
  const [headerEndDate, setHeaderEndDate] = useState(() => moment().add(7, 'days').format('YYYY-MM-DD'));
  const [headerLanguage, setHeaderLanguage] = useState('EN');

  // Get auth headers with debugging
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    const hasToken = !!token;
    
    console.log('🔐 [EventDetail] Auth check:', {
      hasToken,
      tokenPreview: token ? token.substring(0, 20) + '...' : 'No token',
      isAuthenticated,
      currentUser: currentUser?.id || 'No user'
    });

    return hasToken ? {
      headers: {
        Authorization: `Bearer ${token}`
      }
    } : {};
  }, [isAuthenticated, currentUser]);

  // Load user's participation status and groups
  const loadUserEventData = useCallback(async () => {
    if (!isAuthenticated || !id) {
      console.log('🔍 [EventDetail] Skipping user event data load (not authenticated or no ID)');
      setParticipationStatus(null);
      setUserEventGroups(null);
      return;
    }

    try {
      console.log(`🔍 [EventDetail] Loading user event data for event ${id}...`);
      
      // Get participation status
      try {
        const participationResponse = await axios.get(
          `${API_URL}/events/${id}/participation`,
          getAuthHeaders()
        );
        
        const status = participationResponse.data?.participationStatus || null;
        setParticipationStatus(status);
        console.log(`✅ [EventDetail] Participation status: ${status}`);
      } catch (participationError) {
        console.warn('⚠️ [EventDetail] Could not load participation status:', participationError.message);
        setParticipationStatus(null);
      }

      // Get user's event groups
      try {
        const groupsResponse = await axios.get(
          `${API_URL}/events/${id}/groups`,
          getAuthHeaders()
        );
        
        setUserEventGroups(groupsResponse.data);
        console.log(`✅ [EventDetail] User groups loaded:`, groupsResponse.data);
      } catch (groupsError) {
        console.warn('⚠️ [EventDetail] Could not load user groups:', groupsError.message);
        setUserEventGroups(null);
      }
      
    } catch (err) {
      console.error('❌ [EventDetail] Error loading user event data:', err);
      setParticipationStatus(null);
      setUserEventGroups(null);
    }
  }, [id, isAuthenticated, getAuthHeaders]);

  // Callback for when participation status changes
  const handleParticipationChange = useCallback((newStatus) => {
    console.log(`🔄 [EventDetail] Participation status changed to: ${newStatus}`);
    setParticipationStatus(newStatus);
    
    // Trigger participants refresh with a slight delay to allow backend to process
    setTimeout(() => {
      console.log(`🔄 [EventDetail] Triggering participants refresh after status change to: ${newStatus}`);
      setParticipantsRefreshTrigger(prev => prev + 1);
    }, 500);
    
    // Reload user event data to get updated groups
    setTimeout(() => {
      loadUserEventData();
    }, 1000);
  }, [loadUserEventData]);

  // Test API connectivity
  const testAPIConnectivity = useCallback(async () => {
    try {
      console.log('🔍 [EventDetail] Testing API connectivity...');
      
      // Test basic health endpoint
      const healthResponse = await axios.get(`${API_URL}/health`);
      console.log('✅ [EventDetail] API health check passed:', healthResponse.status);
      
      // Test debug endpoint
      const debugResponse = await axios.get(`${API_URL}/debug`);
      console.log('✅ [EventDetail] API debug info:', debugResponse.data);
      
      // Test chat endpoint if authenticated
      if (isAuthenticated && id) {
        try {
          const chatTestResponse = await axios.get(`${API_URL}/chat-test/${id}`);
          console.log('✅ [EventDetail] Chat routes available:', chatTestResponse.data);
        } catch (chatError) {
          console.warn('⚠️ [EventDetail] Chat routes test failed:', chatError.message);
        }
      }
      
    } catch (apiError) {
      console.error('❌ [EventDetail] API connectivity test failed:', apiError);
    }
  }, [id, isAuthenticated]);

  // Helper function to format event content
  const formatEventContent = () => {
    if (!eventData) return null;

    // Set locale based on event language
    const isThai = eventData.language === 'TH';
    moment.locale(isThai ? 'th' : 'en');

    // Format dates
    const displayDate = (date) => moment(date).format('LL');
    const startDateFormatted = displayDate(eventData.event_date);
    const endDateFormatted = eventData.event_end_date ? displayDate(eventData.event_end_date) : null;
    const dateDisplayString = endDateFormatted && startDateFormatted !== endDateFormatted
      ? `${startDateFormatted} - ${endDateFormatted}`
      : startDateFormatted;

    // Prepare map data
    const lat = parseFloat(eventData.latitude);
    const lon = parseFloat(eventData.longitude);
    const mapSrc = !isNaN(lat) && !isNaN(lon)
      ? `https://maps.google.com/maps?q=${lat},${lon}&hl=${isThai ? 'th' : 'en'}&z=15&output=embed`
      : '';

    return { isThai, dateDisplayString, mapSrc };
  };

  // --- Fetch Event Data ---
  useEffect(() => {
    const fetchEventData = async () => {
      if (!id) {
        setError('No Event ID provided in URL.');
        setLoading(false);
        return;
      }
      if (!API_URL) {
        setError('API URL is not configured. Set REACT_APP_API_URL in .env');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setEventData(null);

      try {
        console.log(`🔍 [EventDetail] Fetching data for ID: ${id}`);
        console.log(`🔍 [EventDetail] API URL: ${API_URL}`);

        const isReview = String(id).startsWith('review_');
        const isVenue = String(id).startsWith('venue_');
        let data;

        if (isReview) {
          const numericId = String(id).replace('review_', '');
          const response = await axios.get(`${API_URL}/reviews/${numericId}`);
          console.log('✅ [EventDetail] Review API Response:', response.data);
          const r = response.data;
          // Normalize review fields to match event shape expected by this page
          data = {
            ...r,
            _source: 'review',
            event_name: r.place_name_th || r.place_name_en || r.place_name || 'Review',
            event_date: r.created_at,
            event_end_date: null,
            main_image: r.main_image_url,
            event_image_link: r.main_image_url,
            event_description: r.review_content_th || r.review_content_en || r.review_content || '',
            event_location: r.location_name || r.place_name || '',
            language: r.language || 'TH',
          };
        } else if (isVenue) {
          const numericId = String(id).replace('venue_', '');
          const response = await axios.get(`${API_URL}/venues/${numericId}`);
          console.log('✅ [EventDetail] Venue API Response:', response.data);
          const v = response.data;
          const venueImage =
            v.generated_image_url ||
            (Array.isArray(v.venue_images) && v.venue_images[0] &&
              (typeof v.venue_images[0] === 'string' ? v.venue_images[0] : (v.venue_images[0].url || v.venue_images[0].image_url))) ||
            v.generated_thumbnail_url ||
            v.logo_url ||
            null;
          // Normalize venue fields to match the event shape this page expects
          data = {
            ...v,
            _source: 'venue',
            event_name: v.name_th || v.name_en || v.name_cn || 'Venue',
            event_date: null,
            event_end_date: null,
            main_image: venueImage,
            event_image_link: venueImage,
            event_description: v.description_th || v.description_en || v.description_cn || v.summary_th || v.summary_en || '',
            event_location: v.google_address || '',
            language: v.original_language || 'TH',
          };
        } else {
          const response = await axios.get(`${API_URL}/events/${id}`);
          console.log('✅ [EventDetail] API Response:', response.data);
          data = response.data;
        }

        if (data) {
          setEventData(data);
          if (data.language) {
            setHeaderLanguage(data.language);
          }
          console.log(`✅ [EventDetail] Data loaded: ${data.event_name}`);
        } else {
          setError(`Data not found for ID ${id}.`);
          console.warn('⚠️ [EventDetail] API response returned empty data for ID:', id);
        }
      } catch (err) {
        console.error('❌ [EventDetail] Error fetching event details:', err);
        setError(
          err.response?.data?.message ||
          err.message ||
          `Failed to load event details for ID ${id}.`
        );
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
    testAPIConnectivity();
    window.scrollTo(0, 0);
  }, [id, testAPIConnectivity]);

  // Load user event data when authentication status changes
  useEffect(() => {
    if (eventData) {
      loadUserEventData();
    }
  }, [loadUserEventData, eventData]);

  // --- Navigation Handler for Header ---
  const handleNavigateToEvent = useCallback(
    (eventSuggestion) => {
      if (eventSuggestion?.id) {
        console.log(`🔄 [EventDetail] Navigating to event ID from header: ${eventSuggestion.id}`);
        navigate(`/event/${eventSuggestion.id}`);
      } else {
        console.error('❌ [EventDetail] Cannot navigate, event suggestion missing ID:', eventSuggestion);
      }
    },
    [navigate]
  );

  // --- Render Logic ---

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2, fontFamily: "'Montserrat', 'Poppins', sans-serif" }}>
          Loading event details...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box>
        <EventDetailHeader
          type={headerType}
          setType={setHeaderType}
          startDate={headerStartDate}
          setStartDate={setHeaderStartDate}
          endDate={headerEndDate}
          setEndDate={setHeaderEndDate}
          language={headerLanguage}
          setLanguage={setHeaderLanguage}
          onEventNavigate={handleNavigateToEvent}
        />
        <Container maxWidth="md" sx={{ mt: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </Box>
    );
  }

  // Event not found state
  if (!eventData) {
    return (
      <Box>
        <EventDetailHeader
          type={headerType}
          setType={setHeaderType}
          startDate={headerStartDate}
          setStartDate={setHeaderStartDate}
          endDate={headerEndDate}
          setEndDate={setHeaderEndDate}
          language={headerLanguage}
          setLanguage={setHeaderLanguage}
          onEventNavigate={handleNavigateToEvent}
        />
        <Container maxWidth="md" sx={{ mt: 3 }}>
          <Alert severity="warning">Event not found.</Alert>
        </Container>
      </Box>
    );
  }

  // All logged-in users can access both chat rooms
  const canSeeJoiningChat = isAuthenticated;
  const eventContentData = formatEventContent();
  const { isThai, dateDisplayString, mapSrc } = eventContentData || {};

  console.log('🎨 [EventDetail] Rendering main content:', {
    eventName: eventData.event_name,
    participationStatus,
    canSeeJoiningChat,
    userEventGroups: userEventGroups ? 'loaded' : 'not loaded',
    isAuthenticated
  });

  // --- Main Render with Header + Content ---
  return (
    <Box>
      {/* === HEADER === */}
      <EventDetailHeader
        type={headerType}
        setType={setHeaderType}
        startDate={headerStartDate}
        setStartDate={setHeaderStartDate}
        endDate={headerEndDate}
        setEndDate={setHeaderEndDate}
        language={headerLanguage}
        setLanguage={setHeaderLanguage}
        onEventNavigate={handleNavigateToEvent}
      />

      {/* === CONTENT === */}
      <StyledContainer>
        <ContentWrapper>
          <StyledPaper elevation={3}>
            {/* Event Header & Action Buttons */}
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <EventTitle variant="h1">{eventData.event_name}</EventTitle>
              <EventSummary variant="h2">{eventData.event_summary}</EventSummary>
              
              {/* Action Buttons */}
              <EventActionButtons 
                eventId={id} 
                eventTitle={eventData.event_name}
                eventData={eventData} 
                onParticipationChange={handleParticipationChange}
                getAuthHeaders={getAuthHeaders}
              />
            </Box>

            {/* Event Image(s) */}
            {(() => {
              const allImages = [
                eventData.event_image_link,
                ...(eventData.additional_image_links || [])
              ].filter(Boolean);
              return allImages.length > 0 ? (
                <Box mb={4}>
                  <EventImageCarousel
                    images={allImages}
                    altText={`${eventData.event_name} promotional image`}
                  />
                </Box>
              ) : null;
            })()}

            {/* === CHAT SECTIONS === */}
            <Box sx={{ mb: 5 }}>
              <Grid container spacing={3}>
                {/* Interested Chat - Always show (read-only for non-authenticated users) */}
                <Grid item xs={12} md={isAuthenticated && canSeeJoiningChat ? 6 : 12}>
                  <DualGroupChat
                    eventId={parseInt(id)}
                    chatType="interested"
                    title={eventData.language === 'TH' ? 'ความคิดเห็น' : 'Comments'}
                    isAuthenticated={isAuthenticated}
                    getAuthHeaders={getAuthHeaders}
                    userGroups={userEventGroups}
                  />
                </Grid>
                
                {/* Joining Chat - Only show if user is authenticated and "going" */}
                {isAuthenticated && canSeeJoiningChat && (
                  <Grid item xs={12} md={6}>
                    <DualGroupChat
                      eventId={parseInt(id)}
                      chatType="joining"
                      title={eventData.language === 'TH' ? 'ผู้เข้าร่วม' : 'Going'}
                      isAuthenticated={isAuthenticated}
                      getAuthHeaders={getAuthHeaders}
                      userGroups={userEventGroups}
                    />
                  </Grid>
                )}
              </Grid>


            </Box>

            {/* Event Info Grid (Date/Location/Website + Map) */}
            <Grid container spacing={4} mb={5}>
              {/* Left Column: Info Box */}
              <Grid item xs={12} md={4}>
                <InfoBox>
                  <InfoItem>
                    <SectionTitle variant="h3">
                      <CalendarTodayIcon />
                      {isThai ? 'วันที่' : 'Date'}
                    </SectionTitle>
                    <SectionBody>
                      {dateDisplayString}
                    </SectionBody>
                  </InfoItem>

                  <Box sx={{ borderBottom: '1px solid #e0e0e0', my: 3 }} />

                  <InfoItem>
                    <SectionTitle variant="h3">
                      <LocationOnIcon />
                      {isThai ? 'สถานที่' : 'Location'}
                    </SectionTitle>
                    <SectionBody>
                      {eventData.event_location}
                    </SectionBody>
                  </InfoItem>

                  {eventData.website_link && (
                    <>
                      <Box sx={{ borderBottom: '1px solid #e0e0e0', my: 3 }} />
                      <InfoItem>
                        <SectionTitle variant="h3">
                          <LanguageIcon />
                          {isThai ? 'เว็บไซต์' : 'Website'}
                        </SectionTitle>
                        <SectionBody>
                          <StyledLink href={eventData.website_link} target="_blank" rel="noopener noreferrer">
                            {eventData.website_link}
                          </StyledLink>
                        </SectionBody>
                      </InfoItem>
                    </>
                  )}
                </InfoBox>
              </Grid>

              {/* Right Column: Map */}
              <Grid item xs={12} md={8}>
                {mapSrc ? (
                  <MapContainer>
                    <MapHeaderContainer>
                      <SectionTitle variant="h3" sx={{ m: 0 }}>
                        <MapIcon />
                        {isThai ? 'แผนที่' : 'Map'}
                      </SectionTitle>
                    </MapHeaderContainer>
                    <MapFrame
                      title={`${eventData.event_name} Location Map`}
                      src={mapSrc}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </MapContainer>
                ) : (
                  <Box sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f8fafc',
                    borderRadius: '12px',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)',
                    minHeight: '400px'
                  }}>
                    <Typography sx={{ textAlign: 'center', color: '#757575', p: 4 }}>
                      {isThai ? 'ไม่มีข้อมูลแผนที่' : 'Map data not available.'}
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>

            {/* Event Details Section */}
            <DetailsContainer>
              <SectionTitle
                variant="h3"
                sx={{ justifyContent: 'center', mb: 4 }}
              >
                <DescriptionIcon />
                {isThai ? 'รายละเอียด' : 'Details'}
              </SectionTitle>
              <SectionBody sx={{ textAlign: 'center' }}>
                {eventData.event_description}
              </SectionBody>
            </DetailsContainer>

            {/* === PARTICIPANTS SECTION === */}
            <EventParticipants 
              eventId={id} 
              language={eventData.language}
              refreshTrigger={participantsRefreshTrigger}
            />
          </StyledPaper>
        </ContentWrapper>
      </StyledContainer>
    </Box>
  );
};

export default EventDetail;